import React, { useState } from 'react';
import loginBackground from '@/assets/images/login_background.png';
import valiantlytixLogo from '@/assets/images/darkLogo.png';
import { Button, Form, Input, Checkbox, Modal, InputNumber } from 'antd';
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  PlusOutlined,
  DeleteOutlined,
  BuildOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router';
import logo from '@/assets/images/vantageAI.png';
import axios from 'axios';
import { toast } from 'sonner';
import './NewLoginPage.css';
import AuthErrorModal from '@/components/AuthErrorModal';

const EnterpriseSignUpPage = () => {
  const navigate = useNavigate();
  const { VITE_APP_URL: URL } = import.meta.env;

  // ── Company ──────────────────────────────────────────────
  const [clientName, setClientName] = useState('');

  // ── Admin user ───────────────────────────────────────────
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  // ── Departments ──────────────────────────────────────────
  const [departments, setDepartments] = useState<string[]>(['']);

  // ── Enterprise config (static – not exposed to the user) ──
  const adminSeats   = 1;
  const totalCredits = 100;
  const tierId       = 3;
  const teamStrength = 100;
  const durationDays = 365;

  // ── Enterprise config (user-editable) ────────────────────
  const [editorSeats, setEditorSeats] = useState<number | null>(null);
  const [viewerSeats, setViewerSeats] = useState<number | null>(null);

  // ── UI ────────────────────────────────────────────────────
  const [buttonLoading, setButtonLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isPrivacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorTitle, setErrorTitle] = useState<string>('Registration Error');
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  // ── Departments helpers ───────────────────────────────────
  const addDepartment = () => setDepartments(prev => [...prev, '']);
  const removeDepartment = (idx: number) =>
    setDepartments(prev => prev.filter((_, i) => i !== idx));
  const updateDepartment = (idx: number, val: string) =>
    setDepartments(prev => prev.map((d, i) => (i === idx ? val : d)));

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = async () => {
    const newErrors: Record<string, boolean> = {};
    const missing: string[] = [];

    if (!clientName.trim()) { missing.push('Company Name'); newErrors.clientName = true; }
    if (!adminName.trim()) { missing.push('Admin Name'); newErrors.adminName = true; }
    if (!adminEmail.trim()) { missing.push('Admin Email'); newErrors.adminEmail = true; }
    if (!adminPassword) { missing.push('Password'); newErrors.adminPassword = true; }
    const filledDepts = departments.filter(d => d.trim());
    if (filledDepts.length === 0) { missing.push('At least one Department'); newErrors.departments = true; }

    if (missing.length > 0) {
      setFieldErrors(newErrors);
      setErrorTitle('Missing Required Fields');
      setErrorMessage(`Please fill in: ${missing.join(', ')}.`);
      return;
    }

    if (!termsAccepted) {
      setFieldErrors({ termsAccepted: true });
      setErrorTitle('Privacy Policy Required');
      setErrorMessage('Please accept the Privacy Policy to proceed.');
      return;
    }

    if (adminSeats + (editorSeats || 0) < 1) {
      setErrorTitle('Seat Configuration Error');
      setErrorMessage('Enterprise must have at least one AI user (admin or editor).');
      return;
    }

    if (filledDepts.length > 100) {
      setErrorTitle('Department Limit Exceeded');
      setErrorMessage('Departments must be between 1 and 100.');
      return;
    }

    setButtonLoading(true);
    try {
      const response = await axios.post(`${URL}/api/users/onboarding-request`, {
        client: { name: clientName.trim() },
        admin_user: {
          name: adminName.trim(),
          email: adminEmail.trim(),
          password: adminPassword,
        },
        departments: filledDepts.map(name => ({ name })),
        enterprise: {
          admin_seats: adminSeats,
          editor_seats: editorSeats || 0,
          viewer_seats: viewerSeats || 0,
          total_credits: totalCredits,
          tier_id: tierId,
          team_strength: teamStrength,
          duration_days: durationDays,
        },
      });

      // Backend returns HTTP 201 even for business-logic errors — check the body
      if (response.data?.error) {
        setErrorTitle('Submission Failed');
        setErrorMessage(response.data.error);
        return;
      }
      if (response.data?.message && response.data.message.toLowerCase().includes('error')) {
        setErrorTitle('Submission Failed');
        setErrorMessage(response.data.message);
        return;
      }

      toast.success('Enterprise onboarding request submitted! Our team will reach out soon.');
      navigate('/login');
    } catch (err: any) {
      setErrorTitle('Submission Failed');
      if (err.response?.data?.error) {
        setErrorMessage(err.response.data.error);
      } else if (err.response?.data?.message) {
        setErrorMessage(err.response.data.message);
      } else {
        setErrorMessage('An error occurred. Please try again.');
      }
    } finally {
      setButtonLoading(false);
    }
  };

  // ── Icon helpers (reuse same SVG style as NewSignUpPage) ──
  const UserIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.7 5.7C4.90833 5.7 4.23542 5.42292 3.68125 4.86875C3.12708 4.31458 2.85 3.64167 2.85 2.85C2.85 2.05833 3.12708 1.38542 3.68125 0.83125C4.23542 0.277083 4.90833 0 5.7 0C6.49167 0 7.16458 0.277083 7.71875 0.83125C8.27292 1.38542 8.55 2.05833 8.55 2.85C8.55 3.64167 8.27292 4.31458 7.71875 4.86875C7.16458 5.42292 6.49167 5.7 5.7 5.7ZM0 11.4V9.5C0 9.19653 0.0824653 8.90955 0.247396 8.63906C0.412326 8.36858 0.639931 8.14097 0.930208 7.95625C1.6559 7.53403 2.42309 7.21076 3.23178 6.98646C4.04047 6.76215 4.86182 6.65 5.69584 6.65C6.52986 6.65 7.3526 6.76215 8.16406 6.98646C8.97552 7.21076 9.7441 7.53403 10.4698 7.95625C10.7601 8.12778 10.9877 8.35208 11.1526 8.62917C11.3175 8.90625 11.4 9.19653 11.4 9.5V11.4H0Z" fill="#9AA1AD" />
    </svg>
  );
  const MailIcon = () => (
    <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.425 11.4C1.03313 11.4 0.697656 11.2604 0.418594 10.9812C0.139531 10.702 0 10.3663 0 9.97421V1.41946C0 1.02732 0.139531 0.692708 0.418594 0.415625C0.697656 0.138542 1.03313 0 1.425 0H13.775C14.1669 0 14.5023 0.139597 14.7814 0.418791C15.0605 0.697986 15.2 1.03365 15.2 1.42579V9.98054C15.2 10.3727 15.0605 10.7073 14.7814 10.9844C14.5023 11.2615 14.1669 11.4 13.775 11.4H1.425ZM7.6 6.65L1.425 3.10729V9.975H13.775V3.10729L7.6 6.65ZM7.6 4.96771L13.775 1.425H1.425L7.6 4.96771Z" fill="#9AA1AD" />
    </svg>
  );
  const KeyIcon = () => (
    <svg width="19" height="10" viewBox="0 0 19 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4.75 6.65C4.22222 6.65 3.77361 6.46528 3.40417 6.09583C3.03472 5.72639 2.85 5.27778 2.85 4.75C2.85 4.22222 3.03472 3.77361 3.40417 3.40417C3.77361 3.03472 4.22222 2.85 4.75 2.85C5.27778 2.85 5.72639 3.03472 6.09583 3.40417C6.46528 3.77361 6.65 4.22222 6.65 4.75C6.65 5.27778 6.46528 5.72639 6.09583 6.09583C5.72639 6.46528 5.27778 6.65 4.75 6.65ZM4.75 9.5C3.43056 9.5 2.30903 9.03819 1.38542 8.11458C0.461806 7.19097 0 6.06944 0 4.75C0 3.43056 0.461806 2.30903 1.38542 1.38542C2.30903 0.461806 3.43056 0 4.75 0C5.60764 0 6.39931 0.21441 7.125 0.643229C7.85069 1.07205 8.43125 1.64931 8.86667 2.375H15.675L18.05 4.75L14.4875 8.075L12.825 6.8875L11.4 8.075L9.5 6.65H9.10417C8.7875 7.54722 8.22344 8.24653 7.41198 8.74792C6.60052 9.24931 5.71319 9.5 4.75 9.5Z" fill="#9AA1AD" />
    </svg>
  );

  const inputStyle = (hasError?: boolean) => ({
    backgroundColor: '#F8F9FA',
    borderColor: hasError ? '#EF4444' : '#E5E7EB',
    height: 42,
  });

  const labelClass = 'text-sm font-medium text-gray-700 mb-0 block';
  const sectionTitle = (icon: React.ReactNode, text: string) => (
    <div className="flex items-center gap-2 mb-3 mt-5">
      <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-[#fdf2f2]">
        {icon}
      </div>
      <span className="text-[13px] font-semibold text-gray-800 uppercase tracking-wide">{text}</span>
    </div>
  );

  return (
    <div className="flex w-full h-screen bg-[#F8F9FA] font-sans">
      {/* ── Left: Form Panel ── */}
      <div className="w-full lg:w-1/2 h-full flex flex-col items-center relative px-4 sm:px-8 overflow-y-auto">
        <div className="w-full max-w-[480px] flex flex-col items-center my-auto py-8">

          {/* Logo */}
          <div className="w-full flex justify-center mb-8">
            <img src={logo} alt="Vantage AI Logo" className="w-[160px] md:w-[200px] h-auto object-contain" />
          </div>

          {/* Card */}
          <div className="w-full bg-white px-6 sm:px-8 py-8 sm:py-10 rounded-2xl shadow-sm border border-gray-100 flex flex-col">

            {/* Header */}
            <div className="flex flex-col gap-2 mb-4">
              <div className="flex items-center gap-3">
                <BuildOutlined style={{ color: '#A51C1C', fontSize: 18 }} />
                <span className="text-xl md:text-2xl font-semibold tracking-[0.2px] text-gray-900">
                  Enterprise Registration
                </span>
              </div>
              <span className="text-gray-500 text-sm">
                Submit an enterprise onboarding request. Our team will review and activate your account.
              </span>
            </div>

            <div className="w-full h-px bg-gray-100 mb-2" />

            {/* ── SECTION: Company ── */}
            {sectionTitle(<BuildOutlined style={{ color: '#A51C1C', fontSize: 14 }} />, 'Company')}

            <Form.Item label={<span className={labelClass}>Company / Client Name</span>} className="mb-0" layout="vertical">
              <Input
                size="large"
                status={fieldErrors.clientName ? 'error' : undefined}
                style={inputStyle(fieldErrors.clientName)}
                placeholder="ABC Corporation"
                prefix={
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 13.3V2.85H2.85V0H9.975V5.7H13.3V13.3H7.6V10.45H5.7V13.3H0ZM1.425 11.875H2.85V10.45H1.425V11.875ZM1.425 8.7875H2.85V7.3625H1.425V8.7875ZM1.425 5.7H2.85V4.275H1.425V5.7ZM4.275 8.7875H5.7V7.3625H4.275V8.7875ZM4.275 5.7H5.7V4.275H4.275V5.7ZM4.275 2.85H5.7V1.425H4.275V2.85ZM7.125 8.7875H8.55V7.3625H7.125V8.7875ZM7.125 5.7H8.55V4.275H7.125V5.7ZM7.125 2.85H8.55V1.425H7.125V2.85ZM10.45 11.875H11.875V10.45H10.45V11.875ZM10.45 8.7875H11.875V7.3625H10.45V8.7875Z" fill="#9AA1AD" />
                  </svg>
                }
                value={clientName}
                onChange={e => {
                  setClientName(e.target.value);
                  if (fieldErrors.clientName) setFieldErrors(p => ({ ...p, clientName: false }));
                }}
                className={`light-input rounded-lg hover:border-gray-300 focus:border-[#A91D22] ${fieldErrors.clientName ? '!border-red-500' : ''}`}
              />
            </Form.Item>

            {/* ── SECTION: Admin User ── */}
            {sectionTitle(<UserIcon />, 'Admin User')}

            <div className="flex flex-col gap-y-3">
              <Form.Item label={<span className={labelClass}>Admin Full Name</span>} className="mb-0" layout="vertical">
                <Input
                  size="large"
                  status={fieldErrors.adminName ? 'error' : undefined}
                  style={inputStyle(fieldErrors.adminName)}
                  placeholder="Jane Admin"
                  prefix={<UserIcon />}
                  value={adminName}
                  onChange={e => {
                    setAdminName(e.target.value);
                    if (fieldErrors.adminName) setFieldErrors(p => ({ ...p, adminName: false }));
                  }}
                  className={`light-input rounded-lg hover:border-gray-300 focus:border-[#A91D22] ${fieldErrors.adminName ? '!border-red-500' : ''}`}
                />
              </Form.Item>

              <Form.Item label={<span className={labelClass}>Admin Email</span>} className="mb-0" layout="vertical">
                <Input
                  size="large"
                  status={fieldErrors.adminEmail ? 'error' : undefined}
                  style={inputStyle(fieldErrors.adminEmail)}
                  placeholder="admin@company.com"
                  prefix={<MailIcon />}
                  value={adminEmail}
                  onChange={e => {
                    setAdminEmail(e.target.value);
                    if (fieldErrors.adminEmail) setFieldErrors(p => ({ ...p, adminEmail: false }));
                  }}
                  className={`light-input rounded-lg hover:border-gray-300 focus:border-[#A91D22] ${fieldErrors.adminEmail ? '!border-red-500' : ''}`}
                />
              </Form.Item>

              <Form.Item label={<span className={labelClass}>Password</span>} className="mb-0" layout="vertical">
                <Input.Password
                  size="large"
                  status={fieldErrors.adminPassword ? 'error' : undefined}
                  style={inputStyle(fieldErrors.adminPassword)}
                  placeholder="Create a strong password"
                  prefix={<KeyIcon />}
                  iconRender={visible =>
                    visible ? <EyeTwoTone twoToneColor="#A91D22" /> : <EyeInvisibleOutlined className="text-gray-500" />
                  }
                  value={adminPassword}
                  onChange={e => {
                    setAdminPassword(e.target.value);
                    if (fieldErrors.adminPassword) setFieldErrors(p => ({ ...p, adminPassword: false }));
                  }}
                  className={`light-password rounded-lg ${fieldErrors.adminPassword ? '!border-red-500' : ''}`}
                />
              </Form.Item>
            </div>

            {/* ── SECTION: Departments ── */}
            {sectionTitle(
              <TeamOutlined style={{ color: '#A51C1C', fontSize: 14 }} />,
              'Departments'
            )}

            <div className="flex flex-col gap-2">
              {departments.map((dept, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    size="large"
                    status={fieldErrors.departments && !dept.trim() ? 'error' : undefined}
                    style={inputStyle(fieldErrors.departments && !dept.trim())}
                    placeholder={`Department ${idx + 1} (e.g. Sales)`}
                    value={dept}
                    onChange={e => {
                      updateDepartment(idx, e.target.value);
                      if (fieldErrors.departments) setFieldErrors(p => ({ ...p, departments: false }));
                    }}
                    className="light-input rounded-lg hover:border-gray-300 flex-1"
                  />
                  {departments.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDepartment(idx)}
                      className="flex items-center justify-center w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 border border-red-200 text-[#A91D22] transition-colors cursor-pointer"
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </div>
              ))}
              {departments.length < 100 && (
                <button
                  type="button"
                  onClick={addDepartment}
                  className="flex items-center gap-2 text-[13px] font-medium text-[#A91D22] hover:text-[#8a171b] transition-colors cursor-pointer border-none bg-transparent p-0 mt-1 self-start"
                >
                  <PlusOutlined /> Add Department
                </button>
              )}
            </div>

            {/* ── SECTION: Enterprise Config ── */}
            {sectionTitle(
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#A51C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>,
              'Enterprise Configuration'
            )}

            <div className="grid grid-cols-2 gap-3">
              <Form.Item label={<span className={labelClass}>Editor Seats</span>} className="mb-0" layout="vertical">
                <InputNumber
                  size="large"
                  min={0}
                  placeholder="0"
                  style={{ width: '100%', backgroundColor: '#F8F9FA', borderColor: '#E5E7EB', height: 42 }}
                  value={editorSeats}
                  onChange={v => setEditorSeats(v !== null ? Number(v) : null)}
                  className="rounded-lg"
                />
              </Form.Item>

              <Form.Item label={<span className={labelClass}>Viewer Seats</span>} className="mb-0" layout="vertical">
                <InputNumber
                  size="large"
                  min={0}
                  placeholder="0"
                  style={{ width: '100%', backgroundColor: '#F8F9FA', borderColor: '#E5E7EB', height: 42 }}
                  value={viewerSeats}
                  onChange={v => setViewerSeats(v !== null ? Number(v) : null)}
                  className="rounded-lg"
                />
              </Form.Item>
            </div>

            {/* Seat summary hint */}
            <div className="flex items-center gap-2 mt-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 9v5m0-8h.01" stroke="#B45309" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span className="text-xs text-amber-700">
                Specify how many <strong>editor</strong> and <strong>viewer</strong> seats your team needs. Our team will finalise your account setup.
              </span>
            </div>

            {/* ── Terms ── */}
            <div className="flex items-center mt-5 mb-1">
              <Checkbox
                checked={termsAccepted}
                onChange={e => {
                  setTermsAccepted(e.target.checked);
                  if (fieldErrors.termsAccepted) setFieldErrors(p => ({ ...p, termsAccepted: false }));
                }}
                className={`text-sm ${fieldErrors.termsAccepted ? 'text-red-500 font-medium' : 'text-gray-500'}`}
              >
                I agree to the{' '}
                <a
                  onClick={e => { e.preventDefault(); setPrivacyModalOpen(true); }}
                  className="text-[#A91D22] hover:text-[#8a171b] underline cursor-pointer"
                >
                  Privacy Policy
                </a>
              </Checkbox>
            </div>

            {/* ── Submit ── */}
            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              loading={buttonLoading}
              className="w-full h-11 mt-4 text-base font-medium !bg-[#A91D22] hover:!bg-[#8a171b] !border-none rounded-lg shadow-md transition-all flex items-center justify-center"
            >
              Submit Enterprise Request
            </Button>

            {/* ── Divider ── */}
            <div className="flex items-center my-5">
              <div className="flex-grow border-t border-gray-200" />
              <span className="flex-shrink-0 mx-4 text-gray-400 text-[13px]">Already registered?</span>
              <div className="flex-grow border-t border-gray-200" />
            </div>

            {/* ── Back to login ── */}
            <Button
              size="large"
              onClick={() => navigate('/login')}
              className="w-full h-11 text-base font-medium !text-[#A91D22] border !border-[#A91D22] !bg-[#fdf2f2] hover:!bg-[#fce4e4] rounded-lg transition-all flex items-center justify-center gap-2"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.65 13.3V11.875H11.875V1.425H6.65V0H11.875C12.2669 0 12.6023 0.139532 12.8814 0.418594C13.1605 0.697657 13.3 1.03313 13.3 1.425V11.875C13.3 12.2669 13.1605 12.6023 12.8814 12.8814C12.6023 13.1605 12.2669 13.3 11.875 13.3H6.65ZM5.225 9.975L4.21562 8.96562L5.81875 7.3625H0V5.9375H5.81875L4.21562 4.33437L5.225 3.325L8.55 6.65L5.225 9.975Z" fill="#A51C1C" />
              </svg>
              Sign in to your account
            </Button>

            {/* ── Individual signup link ── */}
            <div className="mt-4 text-center">
              <span className="text-gray-400 text-[13px]">Not an enterprise? </span>
              <button
                type="button"
                onClick={() => navigate('/sign-up-user')}
                className="text-[13px] font-medium text-[#A91D22] hover:text-[#8a171b] underline cursor-pointer border-none bg-transparent p-0"
              >
                Sign up as individual
              </button>
            </div>
          </div>

          <span className="text-gray-400 text-xs md:text-sm select-none pb-6 mt-6">
            © 2026 Vantage AI. All rights reserved.
          </span>
        </div>
      </div>

      {/* ── Right: Image Panel ── */}
      <div className="hidden lg:flex w-1/2 relative bg-black items-center justify-center overflow-hidden">
        <img
          src={loginBackground}
          alt="Vantage AI Dashboard Graphic"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute top-16 left-16 z-10">
          <div className="text-white text-[42px] leading-[1.2] font-bold tracking-tight">
            <div>Enterprise-grade AI</div>
            <div>for your entire <span className="text-[#FCA5A5]">team</span></div>
          </div>
          <p className="text-white/60 mt-4 text-[17px] max-w-[320px] leading-relaxed">
            Onboard your organisation, assign roles, and unlock powerful analytics at scale.
          </p>
        </div>
        <div className="absolute bottom-12 right-12 z-10">
          <img src={valiantlytixLogo} alt="Valiantlytix Logo" className="w-[180px] h-auto object-contain" />
        </div>
      </div>

      {/* ── Privacy Policy Modal ── */}
      <Modal
        title={<span className="text-xl font-bold">Privacy Policy for Valiantlytix Sdn. Bhd.</span>}
        open={isPrivacyModalOpen}
        onCancel={() => setPrivacyModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setPrivacyModalOpen(false)} className="!bg-[#A91D22] hover:!bg-[#8a171b] !border-none">
            Close
          </Button>,
        ]}
        width={800}
        centered
      >
        <div className="text-gray-700 text-[14px] space-y-4 max-h-[65vh] overflow-y-auto pr-2 py-2">
          <p><strong>Effective Date:</strong> April 2, 2026 &nbsp;|&nbsp; <strong>Last Updated:</strong> April 2, 2026</p>
          <p>At Valiantlytix Sdn. Bhd. ("Valiantlytix", "we", "us", or "our"), we specialize in AI transformation and data analytics. We are committed to protecting your privacy. This policy outlines how we handle your personal data.</p>
          <h3 className="font-bold text-[16px] text-gray-900 mt-5">1. Personal Data We Collect</h3>
          <p>We collect identity, contact, inquiry, and technical usage data when you engage with our platform or submit onboarding requests.</p>
          <h3 className="font-bold text-[16px] text-gray-900 mt-5">2. Purpose of Data Processing</h3>
          <p>Data is processed to fulfill enterprise requests, manage relationships, and improve service quality.</p>
          <h3 className="font-bold text-[16px] text-gray-900 mt-5">3. Data Security &amp; Integrity</h3>
          <p>We employ high-standard technical and organizational measures to protect your data from unauthorized access, alteration, or disclosure.</p>
          <h3 className="font-bold text-[16px] text-gray-900 mt-5">4. Contact</h3>
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg mt-3 text-sm">
            <p className="mb-1"><strong>Email:</strong> legal@valiantlytix.com</p>
            <p className="mb-0"><strong>Location:</strong> Petaling Jaya, Selangor, Malaysia</p>
          </div>
        </div>
      </Modal>

      {/* ── Error Modal ── */}
      <AuthErrorModal
        open={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        title={errorTitle}
        message={errorMessage || ''}
      />
    </div>
  );
};

export default EnterpriseSignUpPage;
